import { useState, useMemo, useEffect, useRef } from 'react';
import { CalendarEvent } from '../types/calendar';
import { Todo } from '../types/todo';
import { getCalendarDays, isSameDay, isToday, isCurrentMonth } from '../utils/calendar';
import { formatDate } from '../utils/time';
import { getDesignTokens } from '../utils/designTokens';
import { useThemeStore } from '../store/themeStore';

interface DatePickerProps {
  isOpen: boolean;
  currentDate: Date;
  onDateSelect: (date: Date) => void;
  onClose: () => void;
  events: CalendarEvent[];
  todos: Todo[];
  isDarkMode?: boolean;
  triggerElement?: HTMLElement | null; // Element to position relative to
}

export default function DatePicker({
  isOpen,
  currentDate,
  onDateSelect,
  onClose,
  events,
  todos,
  isDarkMode,
  triggerElement,
}: DatePickerProps) {
  const { isDarkMode: storeIsDarkMode } = useThemeStore();
  const currentIsDarkMode = isDarkMode ?? storeIsDarkMode;
  const tokens = getDesignTokens(currentIsDarkMode);
  const pickerRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);

  // Start with the month of the current date
  const [displayMonth, setDisplayMonth] = useState(() => ({
    year: currentDate.getFullYear(),
    month: currentDate.getMonth(),
  }));

  // Update display month when currentDate changes (if picker is opened for a different month)
  useMemo(() => {
    if (isOpen) {
      setDisplayMonth({
        year: currentDate.getFullYear(),
        month: currentDate.getMonth(),
      });
    }
  }, [isOpen, currentDate]);

  // Calculate position relative to trigger element
  useEffect(() => {
    if (isOpen && triggerElement && pickerRef.current) {
      const rect = triggerElement.getBoundingClientRect();
      const pickerHeight = pickerRef.current.offsetHeight;
      const pickerWidth = pickerRef.current.offsetWidth;
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      
      // Calculate center of trigger element
      const triggerCenterX = rect.left + rect.width / 2;
      
      // Position below the trigger element, centered horizontally
      let top = rect.bottom + 8; // 8px gap
      let left = triggerCenterX - pickerWidth / 2; // Center on trigger
      
      // Adjust if would go off bottom of screen
      if (top + pickerHeight > viewportHeight) {
        // Try above instead
        top = rect.top - pickerHeight - 8;
        // If still off screen, position at bottom with margin
        if (top < 0) {
          top = viewportHeight - pickerHeight - 16;
        }
      }
      
      // Adjust if would go off right side of screen
      if (left + pickerWidth > viewportWidth) {
        left = viewportWidth - pickerWidth - 16;
      }
      
      // Adjust if would go off left side of screen
      if (left < 16) {
        left = 16;
      }
      
      setPosition({ top, left });
    } else if (!triggerElement) {
      // Center if no trigger element
      setPosition(null);
    }
  }, [isOpen, triggerElement]);

  // Count events and todos for a specific date
  const getCountsForDate = (date: Date): { eventCount: number; todoCount: number } => {
    const dayStart = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0);
    const dayEnd = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);

    // Count events that overlap this day
    const eventCount = events.filter((event) => {
      const eventStart = event.startTime instanceof Date ? event.startTime : new Date(event.startTime);
      const eventEnd = event.endTime instanceof Date ? event.endTime : new Date(event.endTime);
      // Event overlaps if: eventStart <= dayEnd AND eventEnd >= dayStart
      return eventStart.getTime() <= dayEnd.getTime() && eventEnd.getTime() >= dayStart.getTime();
    }).length;

    // Count open todos due on this day
    const todoCount = todos.filter((todo) => {
      if (todo.status === 'done') return false; // Only count open todos
      if (!todo.dueDateTime) return false;
      // Parse dueDateTime, handling both Date objects and ISO strings
      // If it's an ISO string, extract date components directly to avoid timezone shifts
      let dueDate: Date;
      if (todo.dueDateTime instanceof Date) {
        dueDate = todo.dueDateTime;
      } else if (typeof todo.dueDateTime === 'string') {
        const dueStr = todo.dueDateTime;
        // Check if it's a date-only string (YYYY-MM-DD) or full ISO string
        if (dueStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
          // Date-only string: parse as local date
          const [year, month, day] = dueStr.split('-').map(Number);
          dueDate = new Date(year, month - 1, day);
        } else {
          // Full ISO string: extract date components from UTC to avoid timezone shifts
          const isoDate = new Date(dueStr);
          // Use UTC methods to get the date components, then create local date
          const year = isoDate.getUTCFullYear();
          const month = isoDate.getUTCMonth();
          const day = isoDate.getUTCDate();
          dueDate = new Date(year, month, day);
        }
      } else {
        dueDate = new Date();
      }
      return isSameDay(dueDate, date);
    }).length;

    return { eventCount, todoCount };
  };

  // Navigate to previous month
  const handlePreviousMonth = () => {
    setDisplayMonth((prev) => {
      if (prev.month === 0) {
        return { year: prev.year - 1, month: 11 };
      }
      return { year: prev.year, month: prev.month - 1 };
    });
  };

  // Navigate to next month
  const handleNextMonth = () => {
    setDisplayMonth((prev) => {
      if (prev.month === 11) {
        return { year: prev.year + 1, month: 0 };
      }
      return { year: prev.year, month: prev.month + 1 };
    });
  };

  // Handle date selection
  const handleDateClick = (date: Date) => {
    onDateSelect(date);
    onClose();
  };

  if (!isOpen) return null;

  const calendarDays = getCalendarDays(displayMonth.year, displayMonth.month);
  const monthName = formatDate(new Date(displayMonth.year, displayMonth.month, 1), 'month-year');

  // If positioned relative to trigger, use absolute positioning; otherwise center
  const containerStyle = position
    ? {
        position: 'fixed' as const,
        top: `${position.top}px`,
        left: `${position.left}px`,
        zIndex: 50,
      }
    : {};

  return (
    <div
      className={position ? 'fixed inset-0 bg-black bg-opacity-50 z-40' : 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4'}
      onClick={onClose}
    >
      <div
        ref={pickerRef}
        className={`${tokens.card.bg} ${tokens.card.border} border rounded-landing p-3 ${tokens.card.shadow}`}
        style={{ width: '300px', maxWidth: '100%', ...containerStyle }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Month Header */}
        <div className="flex items-center justify-between mb-3">
          <button
            onClick={handlePreviousMonth}
            className={`${tokens.button.secondary} w-8 h-8 rounded-landing flex items-center justify-center text-sm transition-all active:scale-95 min-h-[44px] min-w-[44px]`}
            aria-label="Previous month"
          >
            ←
          </button>
          <div className={`${tokens.text.primary} font-semibold text-sm`}>{monthName}</div>
          <button
            onClick={handleNextMonth}
            className={`${tokens.button.secondary} w-8 h-8 rounded-landing flex items-center justify-center text-sm transition-all active:scale-95 min-h-[44px] min-w-[44px]`}
            aria-label="Next month"
          >
            →
          </button>
        </div>

        {/* Weekday Headers */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, i) => (
            <div key={i} className={`${tokens.text.muted} text-xs text-center font-medium`}>
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Grid */}
        <div className="grid grid-cols-7 gap-1">
          {calendarDays.map((calendarDate, i) => {
            const day = calendarDate.day;
            const date = calendarDate.date;
            const isDateToday = isToday(date);
            const isSelectedDate = isSameDay(date, currentDate);
            const isInCurrentMonth = isCurrentMonth(date, displayMonth.year, displayMonth.month);
            const { eventCount, todoCount } = getCountsForDate(date);

            return (
              <button
                key={i}
                onClick={() => handleDateClick(date)}
                className={`w-9 h-9 rounded-landing text-xs font-medium flex flex-col items-center justify-center transition-all active:scale-95 relative ${
                  isDateToday
                    ? `${tokens.button.primary} text-white`
                    : isSelectedDate
                    ? `${tokens.button.secondary} ${tokens.text.primary} border-2 ${tokens.border.medium}`
                    : isInCurrentMonth
                    ? `${tokens.button.secondary} ${tokens.text.primary}`
                    : `${tokens.text.muted} opacity-40`
                }`}
              >
                <span>{day}</span>
                {(eventCount > 0 || todoCount > 0) && (
                  <div className="absolute bottom-0.5 left-0 right-0 flex items-center justify-center gap-0.5">
                    {eventCount > 0 && (
                      <span className="text-[8px] bg-blue-500 text-white rounded-full w-3.5 h-3.5 flex items-center justify-center font-bold">
                        {eventCount}
                      </span>
                    )}
                    {todoCount > 0 && (
                      <span className="text-[8px] bg-orange-500 text-white rounded-full w-3.5 h-3.5 flex items-center justify-center font-bold">
                        {todoCount}
                      </span>
                    )}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
